// src/pages/FAQ.jsx
import React from "react";
import { Link } from "react-router-dom";
import "./FAQ.css";

const Section = ({ id, title, children }) => (
  <section id={id} className="faq-section">
    <h2 className="faq-h2">{title}</h2>
    <div className="faq-card">{children}</div>
  </section>
);

const QA = ({ q, children }) => (
  <div className="faq-qa">
    <h3 className="faq-q">{q}</h3>
    <div className="faq-a">{children}</div>
  </div>
);

const FAQ = () => {
  return (
    <div className="legal-page faq-page">
      <header className="faq-hero">
        <h1 className="faq-h1">Frequently Asked Questions</h1>
        <p className="faq-subtitle">
          NexterZone is a platform-managed trade system. We prioritise safety, verification,
          and accountability over informal meetups.
        </p>

        <nav className="faq-nav" aria-label="FAQ sections">
          <a href="#getting-started">Getting started</a>
          <a href="#no-meetups">Meetups & messaging</a>
          <a href="#verification">Verification & stolen items</a>
          <a href="#workflow">Pickup, inspection, delivery</a>
          <a href="#privacy">Privacy & personal info</a>
          <a href="#rules">Rules & prohibited items</a>
          <a href="#help">Help & support</a>
        </nav>
      </header>

      <Section id="getting-started" title="Getting started">
        <QA q="What is NexterZone?">
          <p>
            NexterZone is a trade marketplace where the platform manages the exchange end-to-end
            once an offer is accepted. This reduces scams, unsafe meetups, and the movement of
            suspicious goods.
          </p>
        </QA>

        <QA q="How do trades work, at a high level?">
          <ol className="faq-list">
            <li>A seller lists an item.</li>
            <li>A buyer makes an offer.</li>
            <li>If the seller accepts, the trade enters a managed workflow.</li>
            <li>NexterZone coordinates verification, pickup, inspection, and delivery updates.</li>
          </ol>
          <p className="faq-note">
            The exact steps can differ depending on item risk and location.
          </p>
        </QA>

        <QA q="Why do some trades show as unavailable or disappear from public listings?">
          <p>
            Trades may be hidden from public browsing when they are in review, already accepted,
            scheduled for pickup, under inspection, placed on hold, cancelled, or completed.
            This prevents duplicate offers and reduces confusion.
          </p>
        </QA>
      </Section>

      <Section id="no-meetups" title="Meetups & messaging">
        <QA q="Can buyers and sellers message each other directly?">
          <p>
            No. NexterZone avoids direct buyer–seller messaging to reduce scams, pressure tactics,
            and off-platform arrangements. Trade updates are provided through official NexterZone
            notifications.
          </p>
        </QA>

        <QA q="Can we meet in person to exchange the item?">
          <p>
            Private meetups are not supported for platform-managed trades. Once an offer is accepted,
            the process follows a controlled flow to protect both parties.
          </p>
        </QA>
      </Section>

      <Section id="verification" title="Verification & stolen items (e.g., phones)">
        <QA q="How does NexterZone help prevent stolen phones or devices being traded?">
          <p>
            NexterZone reduces stolen-goods activity through layered controls. Depending on the item
            and risk signals, we may apply one or more of the following:
          </p>
          <ul className="faq-list">
            <li>
              <strong>Evidence requirements:</strong> sellers can be required to provide clear photos,
              video, and condition details before any pickup is scheduled.
            </li>
            <li>
              <strong>Identifier checks:</strong> for devices, we may request identifiers such as IMEI/serial
              numbers and compare them against known patterns and internal duplicate signals.
            </li>
            <li>
              <strong>Risk review holds:</strong> suspicious listings can be paused (“held”) while we request
              additional verification.
            </li>
            <li>
              <strong>Inspection gates:</strong> items can be inspected at pickup and/or a secure hub before
              delivery confirmation.
            </li>
            <li>
              <strong>Audit trail:</strong> key steps create a traceable record of what was submitted,
              reviewed, and verified.
            </li>
          </ul>
          <p className="faq-note">
            No system can guarantee that every stolen item is detected. Our approach is designed to make
            suspicious activity difficult to complete and easier to intercept.
          </p>
        </QA>

        <QA q="What happens if a phone or item is suspected to be stolen?">
          <p>
            The trade can be paused or cancelled. We may request additional proof of ownership and
            supporting evidence. If verification fails, the trade does not proceed.
          </p>
        </QA>

        <QA q="What proof of ownership might be requested?">
          <ul className="faq-list">
            <li>Proof of purchase (where available)</li>
            <li>Matching identifiers (IMEI/serial) with device packaging or receipts</li>
            <li>Clear, current photos/video showing the actual device and its condition</li>
          </ul>
        </QA>

        <QA q="Can I report a suspicious listing?">
          <p>
            Yes. Use the in-app reporting option (or contact support). Provide details such as why the listing
            looks suspicious and any relevant identifiers.
          </p>
          <p className="faq-note">
            For urgent safety concerns or confirmed theft, contact local law enforcement immediately.
          </p>
        </QA>
      </Section>

      <Section id="workflow" title="Pickup, inspection, and delivery">
        <QA q="Who schedules pickup and delivery?">
          <p>
            Pickup and delivery are coordinated through the NexterZone process. Users are guided via
            official updates rather than arranging logistics privately.
          </p>
        </QA>

        <QA q="Why are inspections used?">
          <p>
            Inspections protect both parties by validating that the item matches the listing and the evidence
            submitted. Failed checks can result in cancellation.
          </p>
        </QA>

        <QA q="What if the item does not match the description or fails inspection?">
          <p>
            The trade can be cancelled and may be flagged for further review. Repeat issues can lead to
            account restrictions.
          </p>
        </QA>
      </Section>

      <Section id="privacy" title="Privacy & personal information">
        <QA q="Does NexterZone show my phone number, email, or address to other users?">
          <p>
            No. NexterZone is designed to avoid exposing direct contact details during trades. Sensitive
            logistics information is handled through controlled, authorised access.
          </p>
        </QA>

        <QA q="Why do you limit personal information during trades?">
          <p>
            Limiting personal information reduces fraud, harassment, and off-platform dealing and helps keep
            exchanges safe and verifiable.
          </p>
        </QA>
      </Section>

      <Section id="rules" title="Rules & prohibited items">
        <QA q="What items are not allowed?">
          <p>
            Listings that are illegal, unsafe, counterfeit, or clearly suspicious are not allowed. NexterZone may
            restrict certain high-risk categories or require stricter verification steps.
          </p>
        </QA>

        <QA q="What behaviour can lead to restrictions?">
          <ul className="faq-list">
            <li>Attempting to move trades off-platform</li>
            <li>Repeated failed verification/inspection outcomes</li>
            <li>Misrepresentation of item condition or identity</li>
            <li>Suspicious patterns linked to stolen or fraudulent items</li>
          </ul>
        </QA>

        <QA q="Where can I read the full policies?">
          <p>
            See <Link to="/trust-safety">Trust &amp; Safety</Link>, and our <Link to="/terms">Terms</Link> and{" "}
            <Link to="/privacy">Privacy</Link> pages.
          </p>
        </QA>
      </Section>

      <Section id="help" title="Help & support">
        <QA q="I need help with a trade. What should I do?">
          <p>
            Visit the <Link to="/help">Help Center</Link> for common issues. If the matter is urgent or safety-related,
            contact support through official channels.
          </p>
        </QA>

        <QA q="I’m seeing something unsafe or illegal. What should I do?">
          <p>
            Report it in the app (or through support). For emergencies, contact local authorities first.
          </p>
        </QA>
      </Section>

      <div className="faq-bottom">
        <p className="faq-note">
          Looking for the policy overview? Visit <Link to="/trust-safety">Trust &amp; Safety</Link>.
        </p>
      </div>
    </div>
  );
};

export default FAQ;
